import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { AlertTriangle, Gauge, Hash, MapPin, Tag } from 'lucide-react';
import { api } from '@/shared/api/cliente';
import { ErrorDeApi } from '@/shared/api/problema';
import type { CuentaDeServicio, ListaDeTarifas, Tarifa } from '@/shared/api/tipos';
import { AccionesDelFormulario, Campo, Modal, claseEntrada } from '@/shared/ui/Modal';
import { notificar } from '@/shared/ui/avisos';
import { nombreLegible } from '@/modules/tarifario/nombres';

const GRUPOS_DE_CONSUMO = ['Residencial', 'Comercial', 'Industrial Menor Generación'];

const esquema = z.object({
  numeroCuenta: z.string().trim().min(1, 'El número de cuenta es obligatorio').max(60),
  cuentaContrato: z.string().max(60).optional(),
  medidor: z.string().max(60).optional(),
  direccionServicio: z.string().max(500).optional(),
  destinoTarifario: z.string().max(120).optional(),
  ruralidad: z.string().optional(),
  grupoConsumo: z.string().max(120).optional(),
  tasaComoPorcentaje: z.string().optional(),
});

type Formulario = z.infer<typeof esquema>;

export function DialogoDeCuenta({
  abierto,
  clienteId,
  cuenta,
  alCerrar,
  alGuardar,
}: {
  abierto: boolean;
  clienteId: number;
  cuenta: CuentaDeServicio | null;
  alCerrar: () => void;
  alGuardar: () => void;
}) {
  const editando = cuenta !== null;
  const [errorGeneral, setErrorGeneral] = useState<string | null>(null);

  const tarifario = useQuery({
    queryKey: ['tarifas'],
    queryFn: () => api.get<ListaDeTarifas>('/tarifas'),
    enabled: abierto,
  });

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<Formulario>({
    resolver: zodResolver(esquema),
    defaultValues: {
      numeroCuenta: cuenta?.numeroCuenta ?? '',
      cuentaContrato: cuenta?.cuentaContrato ?? '',
      medidor: cuenta?.medidor ?? '',
      direccionServicio: cuenta?.direccionServicio ?? '',
      destinoTarifario: cuenta?.destinoTarifario ?? '',
      ruralidad: cuenta?.ruralidad ?? '',
      grupoConsumo: cuenta?.grupoConsumo ?? '',
      tasaComoPorcentaje:
        cuenta?.tasaComoPorcentaje == null ? '' : String(cuenta.tasaComoPorcentaje),
    },
  });

  const destino = watch('destinoTarifario');
  const tasaEscrita = watch('tasaComoPorcentaje');

  const tarifas = tarifario.data?.tarifas ?? [];
  const tarifa: Tarifa | undefined = tarifas.find((t) => t.tipoCliente === destino);

  /** La tarifa manda: elegirla pone su tasa y su zona, que es lo que la cajera espera ver. */
  const aplicarTarifa = (t: Tarifa | undefined) => {
    if (!t) return;
    setValue('destinoTarifario', t.tipoCliente, { shouldDirty: true });
    if (t.ruralidad !== 'ambos') {
      setValue('ruralidad', t.ruralidad, { shouldDirty: true });
    }
    if (t.tasa != null) {
      setValue('tasaComoPorcentaje', String(Number((t.tasa * 100).toFixed(4))), {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  };

  const alCambiarZona = (zona: string) => {
    if (!tarifa?.categoria) return;
    aplicarTarifa(tarifas.find((t) => t.categoria === tarifa.categoria && t.ruralidad === zona));
  };
  const tasaDeLaTarifa = tarifa?.tasa == null ? null : tarifa.tasa * 100;

  const escrita = tasaEscrita === '' ? null : Number(tasaEscrita);
  const esEspecial =
    tasaDeLaTarifa != null &&
    escrita != null &&
    Number.isFinite(escrita) &&
    Math.abs(escrita - tasaDeLaTarifa) > 0.00005;

  const enviar = handleSubmit(async (datos) => {
    setErrorGeneral(null);
    try {
      const porcentaje = datos.tasaComoPorcentaje ? Number(datos.tasaComoPorcentaje) : null;

      const cuerpo = {
        numeroCuenta: datos.numeroCuenta.trim(),
        cuentaContrato: datos.cuentaContrato || null,
        medidor: datos.medidor || null,
        direccionServicio: datos.direccionServicio || null,
        tipoConsumo: datos.grupoConsumo || null,
        grupoConsumo: datos.grupoConsumo || null,
        destinoTarifario: datos.destinoTarifario || null,
        ruralidad: datos.ruralidad || null,
        tasaPorcentaje: porcentaje == null ? null : porcentaje / 100,
      };

      if (editando) {
        await api.put<CuentaDeServicio>(`/cuentas/${cuenta.id}`, cuerpo);
      } else {
        await api.post<CuentaDeServicio>(`/clientes/${clienteId}/cuentas`, cuerpo);
      }

      alGuardar();
      alCerrar();
      notificar.exito(`Cuenta ${cuerpo.numeroCuenta} ${editando ? 'actualizada' : 'registrada'}`);
    } catch (causa) {
      const mensaje = causa instanceof ErrorDeApi ? causa.message : 'No se pudo guardar la cuenta';
      setErrorGeneral(mensaje);
      notificar.error(mensaje);
    }
  });

  return (
    <Modal
      abierto={abierto}
      alCerrar={alCerrar}
      titulo={editando ? 'Editar cuenta' : 'Nueva cuenta'}
      subtitulo={editando ? `Cuenta ${cuenta.numeroCuenta}` : 'Un predio con su medidor'}
      icono={Hash}
      ancho="ancho"
      cerrarAlPulsarFuera={false}
    >
      <form onSubmit={enviar} className="space-y-4">
        {errorGeneral && (
          <p
            role="alert"
            className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-900"
          >
            {errorGeneral}
          </p>
        )}

        <div className="grid gap-4 sm:grid-cols-2">
          <Campo
            etiqueta="Número de cuenta"
            requerido
            error={errors.numeroCuenta?.message}
            ayuda={
              editando
                ? 'NO SE PUEDE CAMBIAR: LO ASIGNA LA EMPRESA ELÉCTRICA Y LAS FACTURAS LO LLEVAN IMPRESO'
                : 'EL DE LA PLANILLA ELÉCTRICA'
            }
          >
            <input
              {...register('numeroCuenta')}
              className={`${claseEntrada} importe ${editando ? 'bg-armazon-50' : ''}`}
              placeholder="200036866578"
              readOnly={editando}
            />
          </Campo>

          <Campo etiqueta="Cuenta contrato" error={errors.cuentaContrato?.message}>
            <input
              {...register('cuentaContrato')}
              className={`${claseEntrada} importe`}
              placeholder="200036866578"
            />
          </Campo>

          <Campo etiqueta="Medidor" error={errors.medidor?.message}>
            <input
              {...register('medidor')}
              className={`${claseEntrada} importe`}
              placeholder="194605_01"
            />
          </Campo>

          <Campo etiqueta="Grupo de consumo" error={errors.grupoConsumo?.message}>
            <select {...register('grupoConsumo')} className={claseEntrada}>
              <option value="">SELECCIONAR</option>
              {GRUPOS_DE_CONSUMO.map((g) => (
                <option key={g} value={g}>
                  {g}
                </option>
              ))}
            </select>
          </Campo>
        </div>

        <Campo etiqueta="Dirección del predio" error={errors.direccionServicio?.message}>
          <div className="relative">
            <MapPin
              className="pointer-events-none absolute left-3.5 top-1/2 size-[15px] -translate-y-1/2 text-armazon-400"
              aria-hidden
            />
            <input
              {...register('direccionServicio')}
              className={`${claseEntrada} pl-10`}
              placeholder="Calle principal y secundaria"
            />
          </div>
        </Campo>

        <div className="mt-3 border-t border-armazon-100 pt-3">
          <p className="mb-1 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-armazon-400">
            <Tag className="size-3 shrink-0" aria-hidden />
            Lo que decide el cobro
          </p>
          <p className="mb-3 text-[11px] uppercase leading-relaxed text-armazon-500">
            Cambiar la categoría, la zona o la tasa cambia lo que se le factura a este predio desde
            la siguiente emisión.
          </p>

          <div className="grid gap-4 sm:grid-cols-2">
            <Campo etiqueta="Categoría tarifaria" error={errors.destinoTarifario?.message}>
              <select
                {...register('destinoTarifario', {
                  onChange: (e: React.ChangeEvent<HTMLSelectElement>) =>
                    aplicarTarifa(tarifas.find((t) => t.tipoCliente === e.target.value)),
                })}
                value={destino ?? ''}
                className={claseEntrada}
              >
                <option value="">SELECCIONAR</option>
                {tarifas.map((t) => (
                  <option key={t.id} value={t.tipoCliente}>
                    {nombreLegible(t.tipoCliente).toUpperCase()}
                  </option>
                ))}
              </select>
            </Campo>

            <Campo etiqueta="Zona" error={errors.ruralidad?.message}>
              <select
                {...register('ruralidad', {
                  onChange: (e: React.ChangeEvent<HTMLSelectElement>) => alCambiarZona(e.target.value),
                })}
                className={claseEntrada}
              >
                <option value="">SELECCIONAR</option>
                <option value="urbano">Urbana</option>
                <option value="rural">Rural</option>
              </select>
            </Campo>
          </div>

          <div className="mt-4">
            <Campo
              etiqueta="Tasa mensual"
              error={errors.tasaComoPorcentaje?.message}
              ayuda={
                tasaDeLaTarifa == null
                  ? 'EN PORCENTAJE. VACÍA DEJA QUE EL CARGO LO DECIDA LA TARIFA'
                  : `SU TARIFA COBRA ${tasaDeLaTarifa.toFixed(4)} %`
              }
            >
              <div className="relative">
                <Gauge
                  className="pointer-events-none absolute left-3.5 top-1/2 size-[15px] -translate-y-1/2 text-armazon-400"
                  aria-hidden
                />
                <input
                  {...register('tasaComoPorcentaje')}
                  type="number"
                  step="0.0001"
                  min={0}
                  max={100}
                  className={`${claseEntrada} importe pl-10 pr-8`}
                  placeholder="1.0000"
                />
                <span className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 text-sm text-armazon-400">
                  %
                </span>
              </div>
            </Campo>

            {esEspecial && tasaDeLaTarifa != null && (
              <div
                role="status"
                className="mt-2 flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2"
              >
                <AlertTriangle className="mt-0.5 size-4 shrink-0 text-amber-600" aria-hidden />
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-bold uppercase text-amber-900">Tasa especial</p>
                  <p className="mt-0.5 text-[11px] uppercase leading-relaxed text-amber-800">
                    Se aparta de su tarifa, que cobra {tasaDeLaTarifa.toFixed(4)} %. Este predio
                    dejará de seguir el tarifario: si la tarifa cambia, este no cambia con ella.
                  </p>
                  <button
                    type="button"
                    onClick={() =>
                      setValue('tasaComoPorcentaje', String(tasaDeLaTarifa), {
                        shouldValidate: true,
                      })
                    }
                    className="mt-1.5 text-[11px] font-semibold uppercase text-amber-900 underline underline-offset-2"
                  >
                    Usar la de la tarifa
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        <AccionesDelFormulario
          alCancelar={alCerrar}
          enviando={isSubmitting}
          textoEnviar={editando ? 'Actualizar cuenta' : 'Crear cuenta'}
        />
      </form>
    </Modal>
  );
}
